import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminHeader from '../../components/ui/AdminHeader';
import AdminClientSelector from '../../components/ui/AdminClientSelector';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import InventoryCard from './components/InventoryCard';
import ImageGalleryModal from './components/ImageGalleryModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import BulkActionsBar from './components/BulkActionsBar';
import FilterBar from './components/FilterBar';
import EmptyState from './components/EmptyState';
import QRCodeLabel from '../../components/ui/QRCodeLabel';
import { getClientInventory, getAllInventory, deleteInventoryItem } from '../../services/laseaInventoryService';

const ClientInventoryManagement = () => {
  const navigate = useNavigate();
  const { userRole, user, userProfile } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [galleryItem, setGalleryItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCondition, setSelectedCondition] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [qrLabelItem, setQrLabelItem] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);


  useEffect(() => {
    const storedClient = localStorage.getItem('selectedClient');
    if (storedClient && storedClient !== 'undefined') {
      try {
        setSelectedClient(JSON.parse(storedClient));
      } catch (e) {
        console.error('Failed to parse selectedClient from localStorage', e);
        localStorage.removeItem('selectedClient');
      }
    }

    // Remove localStorage inventory loading - always fetch fresh from Supabase
    // const storedInventory = localStorage.getItem('clientInventory');
    // if (storedInventory) {
    //   setInventoryItems(JSON.parse(storedInventory));
    // }
  }, []);

  useEffect(() => {
    // Use an AbortController so we can cancel stale in-flight fetches
    // when the selected client changes before the previous fetch finishes.
    const controller = new AbortController();
    const { signal } = controller;

    const loadInventory = async () => {
      // Determine which client ID to use
      let clientIdToFetch = null;

      if (userRole === 'admin') {
        // Admin: use selected client if available, otherwise show all
        clientIdToFetch = selectedClient?.id || null;
      } else {
        // Client user: use their own user ID
        clientIdToFetch = user?.id || userProfile?.id;
      }

      // If client user and no user ID available yet, wait
      if (userRole !== 'admin' && !clientIdToFetch) {
        return;
      }

      // Safety-net: if loading gets stuck for more than 30s, clear it.
      const safetyTimer = setTimeout(() => {
        if (!signal.aborted) {
          console.warn('loadInventory safety timeout fired – clearing loading state');
          setLoading(false);
        }
      }, 30000);

      try {
        if (!signal.aborted) setLoading(true);

        // Build filters object
        const filters = {};
        if (selectedCategory && selectedCategory !== 'all') {
          filters.category = selectedCategory;
        }
        if (selectedCondition && selectedCondition !== 'all') {
          filters.condition = selectedCondition;
        }
        if (activeFilter !== 'all') {
          filters.isActive = activeFilter === 'active';
        }

        // Fetch inventory from Supabase
        let inventoryData;
        if (userRole === 'admin' && !clientIdToFetch) {
          // Admin viewing all inventory
          inventoryData = await getAllInventory(filters);
        } else {
          // Admin viewing specific client OR client viewing their own inventory
          inventoryData = await getClientInventory(clientIdToFetch, filters);
        }

        // If the effect was cleaned up while we were awaiting, discard result
        if (signal.aborted) return;

        // Apply search filter
        let filteredData = inventoryData || [];
        if (searchQuery) {
          const query = searchQuery?.toLowerCase();
          filteredData = inventoryData?.filter(item =>
            item?.itemName?.toLowerCase()?.includes(query) ||
            item?.description?.toLowerCase()?.includes(query) ||
            item?.category?.toLowerCase()?.includes(query) ||
            item?.location?.toLowerCase()?.includes(query)
          );
        }

        setInventoryItems(filteredData);
      } catch (error) {
        if (signal.aborted) return; // Ignore errors from cancelled fetches
        console.error('Error loading inventory:', error);
        setInventoryItems([]);
      } finally {
        clearTimeout(safetyTimer);
        // Only update loading state if this effect is still the active one
        if (!signal.aborted) setLoading(false);
      }
    };

    loadInventory();

    return () => {
      // Cancel the in-flight fetch so stale results never update state
      controller.abort();
    };
  }, [selectedClient?.id, selectedCategory, selectedCondition, activeFilter, searchQuery, userRole, user?.id, userProfile?.id]);

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setSelectedItems([]);
  };

  const handleItemSelect = (itemId) => {
    setSelectedItems((prev) =>
      prev?.includes(itemId) ?
        prev?.filter((id) => id !== itemId) :
        [...prev, itemId]
    );
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
  };

  const handleEdit = (item) => {
    localStorage.setItem('editingItem', JSON.stringify(item));
    navigate('/edit-item-information');
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem) return;

    setIsDeleting(true);
    try {
      await deleteInventoryItem(deleteItem?.id);

      const updatedItems = inventoryItems?.filter((item) => item?.id !== deleteItem?.id);
      setInventoryItems(updatedItems);
      // Remove localStorage inventory caching to rely on fresh data
      // localStorage.setItem('clientInventory', JSON.stringify(updatedItems));

      setSelectedItems((prev) => prev?.filter((id) => id !== deleteItem?.id));
      setDeleteItem(null);

      // Trigger update of client counts in sidebar
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedItems?.length === 0) return;
    const firstItem = inventoryItems?.find((item) => item?.id === selectedItems?.[0]);
    setDeleteItem({ ...firstItem, isBulk: true, count: selectedItems?.length });
  };

  const handleConfirmBulkDelete = async () => {
    setIsDeleting(true);
    try {
      // Execute deletions in parallel
      await Promise.all(selectedItems.map(id => deleteInventoryItem(id)));

      const updatedItems = inventoryItems?.filter((item) => !selectedItems?.includes(item?.id));
      setInventoryItems(updatedItems);
      // Remove localStorage inventory caching
      // localStorage.setItem('clientInventory', JSON.stringify(updatedItems));

      setSelectedItems([]);
      setDeleteItem(null);

      // Trigger update of client counts in sidebar
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting items:', error);
      alert('Failed to delete some items. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkStatusUpdate = (newStatus) => {
    const updatedItems = inventoryItems?.map((item) =>
      selectedItems?.includes(item?.id) ?
        { ...item, status: newStatus?.charAt(0)?.toUpperCase() + newStatus?.slice(1) } :
        item
    );
    setInventoryItems(updatedItems);
    localStorage.setItem('clientInventory', JSON.stringify(updatedItems));
    setSelectedItems([]);
  };

  const handleViewImages = (item) => {
    setGalleryItem(item);
  };

  const handlePrintQR = (item) => {
    setQrLabelItem(item);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setSortBy('newest');
  };

  const filteredAndSortedItems = inventoryItems?.filter((item) => {
    const matchesSearch = item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      item?.liin?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item?.status?.toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  })?.sort((a, b) => {
    switch (sortBy) {
      case 'oldest':
        return new Date(a.arrivalDate) - new Date(b.arrivalDate);
      case 'name-asc':
        return a?.name?.localeCompare(b?.name);
      case 'name-desc':
        return b?.name?.localeCompare(a?.name);
      case 'newest':
      default:
        return new Date(b.arrivalDate) - new Date(a.arrivalDate);
    }
  });

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || sortBy !== 'newest';



  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <div className="flex pt-16">
        <AdminClientSelector
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onClientSelect={handleClientSelect}
          refreshTrigger={refreshTrigger}
        />


        <main className="flex-1 p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-2">Inventory Management</h1>
                {selectedClient &&
                  <p className="text-sm text-muted-foreground">
                    Managing inventory for <span className="font-semibold text-foreground">{selectedClient?.name}</span>
                  </p>
                }
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="lg:hidden p-2 rounded-md hover:bg-muted transition-smooth touch-target"
                  aria-label="Open client selector">

                  <Icon name="Menu" size={24} />
                </button>

                <Button
                  variant="default"
                  onClick={() => navigate('/add-inventory-item')}
                  iconName="Plus"
                  iconPosition="left"
                  iconSize={20}>

                  Add New Item
                </Button>
              </div>
            </div>

            {!selectedClient ?
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                  <Icon name="Users" size={40} color="var(--color-primary)" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Select a Client</h3>
                <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
                  Choose a client from the sidebar to view and manage their inventory items.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setIsSidebarOpen(true)}
                  iconName="Users"
                  iconPosition="left"
                  iconSize={20}>

                  Open Client List
                </Button>
              </div> :

              <>
                <FilterBar
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  statusFilter={statusFilter}
                  onStatusChange={setStatusFilter}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  onClearFilters={handleClearFilters} />


                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="text-center">
                      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-sm text-muted-foreground">Loading inventory...</p>
                    </div>
                  </div>
                ) : inventoryItems?.length === 0 ? (
                  <EmptyState
                    hasFilters={false}
                    onClearFilters={handleClearFilters} />
                ) : filteredAndSortedItems?.length === 0 ? (
                  <EmptyState
                    hasFilters={hasActiveFilters}
                    onClearFilters={handleClearFilters} />
                ) : (
                  <>
                    <div className="mb-4 flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">
                        Showing {filteredAndSortedItems?.length} item{filteredAndSortedItems?.length !== 1 ? 's' : ''}
                      </p>
                      {selectedItems?.length > 0 &&
                        <p className="text-sm font-medium text-primary">
                          {selectedItems?.length} selected
                        </p>
                      }
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-24">
                      {filteredAndSortedItems?.map((item) =>
                        <InventoryCard
                          key={item?.id}
                          item={item}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onViewImages={handleViewImages}
                          onPrintQR={handlePrintQR}
                          isSelected={selectedItems?.includes(item?.id)}
                          onSelect={handleItemSelect} />

                      )}
                    </div>
                  </>
                )}
              </>
            }
          </div>
        </main>
      </div>
      <BulkActionsBar
        selectedCount={selectedItems?.length}
        onClearSelection={handleClearSelection}
        onBulkStatusUpdate={handleBulkStatusUpdate}
        onBulkDelete={handleBulkDelete} />

      <ImageGalleryModal
        isOpen={!!galleryItem}
        onClose={() => setGalleryItem(null)}
        item={galleryItem} />

      <DeleteConfirmationModal
        isOpen={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        onConfirm={deleteItem?.isBulk ? handleConfirmBulkDelete : handleConfirmDelete}
        item={deleteItem}
        itemName={deleteItem?.name}
        isBulk={deleteItem?.isBulk}
        count={deleteItem?.count}
        isDeleting={isDeleting}
      />

      {qrLabelItem && (
        <QRCodeLabel
          serialNumber={qrLabelItem?.id}
          itemName={qrLabelItem?.name}
          onClose={() => setQrLabelItem(null)}
        />
      )}
    </div>);

};

export default ClientInventoryManagement;